import { useCallback, useMemo } from 'react';
import type { DmMessagePatientRow } from '../../../services/dmMessagesInbox';
import { useLeavingIds } from '../shared/useLeavingIds';
import { buildMessageRows, messageLeaveKey } from './messageRows';

// Reviewed patients fold out of the table, then stay hidden until a refetch confirms they're gone.
export function useLeavingMessageRows(patients: readonly DmMessagePatientRow[]) {
    const presentKeys = useMemo(() => patients.map(messageLeaveKey), [patients]);
    const { leavingIds, hiddenIds, markLeaving } = useLeavingIds(presentKeys);

    const rows = useMemo(
        () => buildMessageRows(patients, leavingIds, hiddenIds),
        [patients, leavingIds, hiddenIds]
    );

    const markPatientLeaving = useCallback(
        (patientId: string): Promise<void> => {
            const patient = patients.find((row) => row.patientId === patientId);
            return patient ? markLeaving(messageLeaveKey(patient)) : Promise.resolve();
        },
        [patients, markLeaving]
    );

    // The patient's row while it's still listed; null once it's leaving, hidden or not loaded.
    const findListedPatient = useCallback(
        (patientId: string): DmMessagePatientRow | null =>
            rows.find((row) => row.id === patientId && !row.leaving)?.patient ?? null,
        [rows]
    );

    return { rows, markPatientLeaving, findListedPatient };
}
